import { useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import { StudentLayout } from '../components/StudentLayout';
import type { ExamScheduleDto } from '../types/models';

const fmtTime = (iso: string) => new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

/** "In 2 days" / "In 5 hours" until a future time. */
function fmtBadge(ms: number): string {
  const mins = Math.round(ms / 60000);
  const d = Math.floor(mins / 1440);
  const h = Math.floor(mins / 60);
  if (d >= 1) return `In ${d} day${d === 1 ? '' : 's'}`;
  if (h >= 1) return `In ${h} hour${h === 1 ? '' : 's'}`;
  return 'Soon';
}

function statusBadge(s: ExamScheduleDto, now: number) {
  const start = new Date(s.scheduledStart).getTime();
  const end = new Date(s.scheduledEnd).getTime();
  if (now < start) return { kind: 'soon', label: fmtBadge(start - now) };
  if (now <= end) return { kind: 'active', label: 'Active now' };
  return { kind: 'missed', label: 'Completed' };
}

export function StudentSchedulePage() {
  const [rows, setRows] = useState<ExamScheduleDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.listMyExamSchedules().then(setRows).catch(e => setError(e?.message ?? 'Failed to load schedule.'));
  }, []);

  const subtitle = useMemo(() => {
    const r = rows?.[0];
    return r ? [r.batchName, r.departmentName].filter(Boolean).join(' · ') : '';
  }, [rows]);

  // Group the schedule by exam (e.g. "Internal Exam II") — one exam can hold several subjects.
  // Subjects within an exam are ordered by time; exams are ordered by their earliest subject.
  const examGroups = useMemo(() => {
    const m = new Map<string, ExamScheduleDto[]>();
    for (const r of rows ?? []) {
      const key = r.examName || 'Exam';
      (m.get(key) ?? m.set(key, []).get(key)!).push(r);
    }
    const startMs = (r: ExamScheduleDto) => new Date(r.scheduledStart).getTime();
    return [...m.entries()]
      .map(([examName, list]) => ({ examName, list: list.slice().sort((a, b) => startMs(a) - startMs(b)) }))
      .sort((a, b) => startMs(a.list[0]) - startMs(b.list[0]));
  }, [rows]);

  const now = Date.now();

  const scheduleRow = (r: ExamScheduleDto) => {
    const d = new Date(r.scheduledStart);
    const day = d.toLocaleDateString(undefined, { day: '2-digit' });
    const mon = d.toLocaleDateString(undefined, { month: 'short' }).toUpperCase();
    const st = statusBadge(r, now);
    return (
      <tr key={r.id}>
        <td>
          <div className="sched-date">
            <span className="sched-day">{day}</span>
            <span className="sched-mon">{mon}</span>
          </div>
        </td>
        <td>
          <div style={{ fontWeight: 600 }}>{r.subjectName}</div>
          {r.subjectCode && <div className="muted slug">{r.subjectCode}</div>}
        </td>
        <td>🕑 {fmtTime(r.scheduledStart)} — {fmtTime(r.scheduledEnd)}</td>
        <td>🕑 {r.durationMinutes} min</td>
        <td><span className={`exam-badge ${st.kind}`}>● {st.label}</span></td>
      </tr>
    );
  };

  return (
    <StudentLayout>
      <div className="dash-section-head" style={{ marginBottom: 6 }}>
        <div>
          <h1 style={{ margin: 0 }}>My exam schedule</h1>
          {subtitle && <div className="muted" style={{ marginTop: 2 }}>{subtitle}</div>}
        </div>
      </div>

      {error && <div className="card error">{error}</div>}
      {!rows && !error && <div className="card muted">Loading…</div>}
      {rows && rows.length === 0 && (
        <div className="card muted">No exams have been scheduled for your batch yet.</div>
      )}

      {rows && rows.length > 0 && examGroups.map(g => (
        <section key={g.examName} className="dash-section" style={{ marginBottom: 20 }}>
          <div className="sched-exam-head">
            <h2 style={{ margin: 0 }}>{g.examName}</h2>
            <span className="muted">{g.list.length} subject{g.list.length === 1 ? '' : 's'}</span>
          </div>
          <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
            <table className="data-table sched-table">
              <thead>
                <tr>
                  <th style={{ width: 90 }}>Date</th>
                  <th>Subject</th>
                  <th>Time Window</th>
                  <th>Duration</th>
                  <th style={{ width: 150 }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {g.list.map(scheduleRow)}
              </tbody>
            </table>
          </div>
        </section>
      ))}

      <div className="card" style={{ borderLeft: '4px solid var(--accent)' }}>
        <div className="muted">
          ℹ Exams are published by your faculty. This schedule updates automatically when new exams are added.
        </div>
      </div>
    </StudentLayout>
  );
}
